import type { FC } from "react";
import { useEffect, useState } from "react";
import _ from "lodash";
import styled from "styled-components";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { QueryBarItem } from "../QueryBarItem";
import { PageOverlay } from "../WebsiteQueryBar/WebsiteQueryBar";
import { KeywordsQueryBarAutocomplete } from "./components/KeywordsQueryBarAutocomplete";
import { QueryBarItemContainer } from "../WebsiteQueryBar/EmptyWebsiteQueryBar";
import { KeywordLoaderContainer } from "./KeywordsQueryBarStyles";
import { ImageItemContainer, PixelPlaceholderLoader, QueryBarWebsiteItem } from "@similarweb/ui-components";
import { queryBarItemTypes } from "app/components/React/HOCs/accessWithoutMandatoryParamsHOC";
import { i18nFilter } from "app/filters/i18nFilter";
import { useCurrentStateAndParams } from "@uirouter/react";
import type { IKeywordGroup } from "app/common/keyword-research/monitor-lists/monitorKeywordsTypes";
import { showSuccessToast } from "app/actions/toast_actions";
import { useDispatch } from "react-redux";
import type { QueryBarItemMode } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";

const i18n = i18nFilter();

const LARGE_AUTOCOMPLETE_WIDTH = "686px";
const SMALL_AUTOCOMPLETE_WIDTH = "320px";

export const KeywordsQueryBarAutocompleteStyled = styled(KeywordsQueryBarAutocomplete)<{
    $isLargeBar: boolean;
}>`
    width: ${({ $isLargeBar }) => ($isLargeBar ? LARGE_AUTOCOMPLETE_WIDTH : SMALL_AUTOCOMPLETE_WIDTH)};
`;

const KeywordsQueryBarItemContainer = styled(QueryBarItemContainer)`
    .QueryBarAutoComplete {
        z-index: 10000;
    }
`;

const ImageItemContainerStyled = styled(ImageItemContainer)`
    svg > path {
        fill: ${colorsPalettes.blue[400]};
    }
`;

const queryItemImageRenderer = () => {
    return (
        <ImageItemContainerStyled borderColor={colorsPalettes.blue[400]}>
            <SWReactIcons
                iconName="search-keywords"
                size="xs"
            />
        </ImageItemContainerStyled>
    );
};

export const EmptyKeywordsQueryBar: FC<{
    isLoading: boolean;
    mode: QueryBarItemMode;
    showAutoCreateListCTA?: boolean;
    showGoToMonitorKeywordListButton?: boolean;
    shouldConfirmDeleteKeywordList?: boolean;
    showKeywordGroups?: boolean;
}> = ({
    isLoading,
    mode,
    showAutoCreateListCTA = true,
    showGoToMonitorKeywordListButton = false,
    shouldConfirmDeleteKeywordList = false,
    showKeywordGroups = true,
}) => {
    const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(true);
    const { params, state } = useCurrentStateAndParams();
    const dispatch = useDispatch();
    const onItemSelected = (item) => {
        proNavigator.updateQueryParams(
            { keyword: item.Id ? `*${item.Id}` : (item.name ?? item) },
            {
                location: "replace",
                reload: false,
            },
        );
    };

    useEffect(() => {
        // when the page changes open the overlay by default
        setIsOverlayOpen(true);
    }, [state.name]);

    const onOverlayClick = () => {
        setIsOverlayOpen(false);
    };

    const showListCreatedSuccessfullyToast = (newlyCreatedList: IKeywordGroup) => {
        dispatch(showSuccessToast(i18n("create.list.wizard.toast", { listName: newlyCreatedList.Name })));
    };

    const refreshPageToNewList = (newlyCreatedList: IKeywordGroup) => {
        if (newlyCreatedList?.Id) {
            void proNavigator.go(
                state.name,
                {
                    ...params,
                    keyword: `*${newlyCreatedList.Id}`,
                },
                { reload: true },
            );
        }
    };

    const onSavedList = (savedList: IKeywordGroup) => {
        showListCreatedSuccessfullyToast(savedList);
        refreshPageToNewList(savedList);
    };

    return (
        <>
            <KeywordsQueryBarItemContainer>
                <QueryBarItem
                    id="FakeKeywordQueryBar"
                    activeItem={isOverlayOpen ? "FakeKeywordQueryBar" : ""}
                    isLoading={isLoading}
                    renderComponent={
                        <QueryBarWebsiteItem
                            text={i18n("querybar.missing.param.state.query.placeholder", {
                                itemType: i18n(queryBarItemTypes.keyword),
                            })}
                            isCompare={false}
                            onItemClick={() => setIsOverlayOpen(true)}
                            renderImageComponent={queryItemImageRenderer}
                            image=""
                        />
                    }
                    searchComponent={
                        <KeywordsQueryBarAutocompleteStyled
                            $isLargeBar={showKeywordGroups}
                            showKeywords
                            mode={mode}
                            onItemClick={onItemSelected}
                            selectedValue=""
                            className={"QueryBarAutoComplete"}
                            onGroupDelete={_.noop}
                            truncateResultsOnBodyClick={false}
                            showAutoCreateListCTA={showAutoCreateListCTA}
                            onSaveKeywordList={onSavedList}
                            showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
                            shouldConfirmDeleteKeywordList={shouldConfirmDeleteKeywordList}
                        />
                    }
                    loadingComponent={
                        <KeywordLoaderContainer>
                            <PixelPlaceholderLoader
                                width={140}
                                height={26}
                            />
                        </KeywordLoaderContainer>
                    }
                />
            </KeywordsQueryBarItemContainer>
            <div onClick={onOverlayClick}>
                <PageOverlay
                    isOpen={isOverlayOpen}
                    hideSidebarOverlay
                />
            </div>
        </>
    );
};
